import { Injectable } from '@angular/core';

import { UserService } from '@shared/services';
import { ActivityReportSimplified, ActivityReportEEProfileSimplified, VacationRequest     } from '@shared/factories';
import { OverviewActivityReports, StatusCellExtras, StatusModel, OverviewVacationRequests } from 'app/models/misc.model';

@Injectable({
  providedIn: 'root'
})
export class StatusService {
  constructor(
    private userService: UserService
  ) { }

  getStatusActivityReport(ar: OverviewActivityReports, extra: StatusCellExtras): StatusModel {
    if (extra.paginationId === 'failed-erp') return this.getFailedERPStatus(ar as ActivityReportSimplified);
    else                                     return this.getActivityReportStatus(ar);
  }

  getStatusVacationRequest(vr: OverviewVacationRequests, extra: StatusCellExtras) {
    if (extra.paginationId === 'vr-list') return this.getVacationRequestStatus(vr);
    else                                  return this.getFailedERPStatus(vr as VacationRequest);
  }

  private getFailedERPStatus(entry: ActivityReportSimplified | VacationRequest): StatusModel {
    return {
      color: this.getFailedERPDotColor(entry),
      label: this.getFailedERPDotText(entry)
    };
  }

  private getActivityReportStatus(ar: OverviewActivityReports): StatusModel {
    return {
      color: this.activityReportStateColor(ar),
      label: this.activityReportStateText(ar)
    };
  }

  private getVacationRequestStatus(vr: OverviewVacationRequests): StatusModel {
    return {
      color: this.vacationRequestStateColor(vr),
      label: this.vacationRequestStateText(vr)
    };
  }

  private getFailedERPDotColor(entry: ActivityReportSimplified | VacationRequest): string {
    let state = (entry as ActivityReportSimplified).export_state || (entry as VacationRequest).exportState;
    switch (state) {
      case 'ready_to_export':    { return 'grey';   };
      case 'successful_export':  { return 'green';  };
      case 'failed_export':      { return 'red';    };
      case 'resolved_manually':  { return 'blue';   };
      case 'resolved_by_system': { return 'orange'; };
      default:                   { return 'grey';   };
    };
  }

  private getFailedERPDotText(entry: ActivityReportSimplified | VacationRequest): string {
    let state   = (entry as ActivityReportSimplified).export_state   || (entry as VacationRequest).exportState;
    let comment = (entry as ActivityReportSimplified).export_comment || (entry as VacationRequest).exportComment;

    switch (state) {
      case 'ready_to_export':    { return 'Übertragung ausstehend';  };
      case 'successful_export':  { return 'Übertragung erfolgreich'; };
      case 'failed_export':      { return  comment;                  };
      case 'resolved_manually':  { return 'Manuell erledigt';        };
      case 'resolved_by_system': { return 'Automatisch erledigt';    };
      default:                   { return 'Übertragung ausstehend';  };
    };
  }

  initialDateState(ar: OverviewActivityReports): StatusModel {
    if (this.userService.isInternal) return this.initialDateStateInternal(ar);
    if (this.userService.isCustomer) return this.getActivityReportStatus(ar);
  }

  private initialDateStateInternal(ar: OverviewActivityReports): StatusModel {
    if (ar?.approvedByCustomerPortals) return { color: 'grey bold', label: 'Von Kunden freigegeben' };
    if (ar?.rejectedByCustomerPortals) return { color: 'grey bold', label: 'Von Kunden abgelehnt'   };
  }

  private activityReportStateColor(ar: OverviewActivityReports): string {
    if (ar instanceof ActivityReportEEProfileSimplified) {
      if (ar?.archivedBySystem  ) return 'grey';
      if (ar?.archivedByExternal) return 'grey';
      if (ar?.archivedByInternal) return 'grey';
      if (ar?.approvedByInternal) return 'grey';
      if (ar?.rejectedByInternal) return 'grey';
    }

    if (ar?.approvedByCustomer)  return 'green';
    if (ar?.rejectedByCustomer)  return 'red';
    if (ar?.awaitingForCustomer) return 'orange';
    return 'grey';
  }

  private vacationRequestStateColor(vr: OverviewVacationRequests): string {
    switch (vr.state) {
      case 'approved_by_internal':
      case 'rejected_by_internal':
      case 'archived_by_internal':
      case 'archived_by_system':         { return 'grey'    };
      case 'awaiting_customer_approval':
      case 'awaiting_external_approval': { return 'orange'; };
      case 'approved_by_customer':
      case 'approved_by_external':
      case 'awaiting_internal_approval': { return 'green';  };
      case 'rejected_by_customer':
      case 'rejected_by_external':       { return 'red';    };
      default:                           { return 'grey'    };
    };
  }

  private activityReportStateText(ar: OverviewActivityReports): string {
    if (this.userService.isInternal) return this.activityReportStateTextInternal(ar);
    if (this.userService.isCustomer) return this.activityReportStateTextCustomer(ar);
  }

  private vacationRequestStateText(vr: OverviewVacationRequests): string {
    if (vr?.approvedByInternal) return 'Freigegeben von Tempton';
    if (vr?.rejectedByInternal) return 'Abgelehnt von Tempton';
    if (vr?.archivedByInternal) return 'Archiviert von Tempton';
    if (vr?.archivedByExternal) return 'Archiviert von Mitarbeiter';
    if (vr?.archivedBySystem  ) return 'Automatisch archiviert';

    if (this.userService.isInternal) return vr.mappedStateInternal;
    if (this.userService.isCustomer) return vr.mappedStateCustomer;
  }

  private activityReportStateTextInternal(ar: OverviewActivityReports): string {
    if (ar instanceof ActivityReportEEProfileSimplified) {
      if (ar?.archivedBySystem  ) return 'Automatisch archiviert';
      if (ar.archivedByExternal ) return 'Archiviert von Mitarbeiter';
      if (ar.archivedByInternal ) return 'Archiviert von Tempton';
      if (ar?.approvedByInternal) return 'Freigegeben von Tempton';
      if (ar?.rejectedByInternal) return 'Abgelehnt von Tempton';
    }

    let text: string;
    if (ar?.approvedByCustomer)  text = 'Tätigkeitsnachweise Kundenfreigabe vorhanden';
    if (ar?.rejectedByCustomer)  text = 'Tätigkeitsnachweise Klärungsbedarf';
    if (ar?.awaitingForCustomer) text = 'Warten auf TN Kundenfreigabe';

    if (ar instanceof ActivityReportEEProfileSimplified) text = text?.replace('Tätigkeitsnachweise ', '');
    return text;
  }

  private activityReportStateTextCustomer(ar: OverviewActivityReports): string {
    if (ar?.approvedByCustomer)  return 'Von Ihnen freigegeben';
    if (ar?.rejectedByCustomer)  return 'Von Ihnen abgelehnt';
    if (ar?.awaitingForCustomer) return 'Ihre Freigabe erwartet';
  }

}
